import React, {useEffect, useState} from 'react';
import {Grid, useTheme} from '@mui/material';
import CheckboxTree from 'react-checkbox-tree';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';
import {useSnackbar} from 'notistack';
import EncryptUtils from 'src/utils/EncryptUtils';
import {getDicomStudiesData} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {LoaderGrid} from '@components/clinical-report-documents/ClinicalReportDocumentStyles';
import ComponentLoader from '@components/loaders/component-loader/ComponentLoader';
import {useTranslation} from 'react-i18next';
import {useSelectedDicomContext} from '@context/SelectedDicomContext';

const ClinicalReportDicom = (props: any): React.ReactElement => {
  const theme = useTheme();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const {SelectedDicomState, setSelectedDicomData} = useSelectedDicomContext();
  const {subModuleUIData} = props;

  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [nodesData, setNodesData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch folders
  const fetchFolders = async () => {
    setIsLoading(true);
    try {
      const data: any = {
        patient_id: props?.patient_id,
        sql_data: subModuleUIData
      };

      const encryptRequestData = EncryptUtils.encryptedData(data);
      const response = await getDicomStudiesData({tk: encryptRequestData});

      const transformedData = response?.data?.map((folder: any) => ({
        value: folder?.LegacyApp,
        label: folder?.LegacyApp,
        children: []
      }));

      setNodesData(transformedData);
      return transformedData;
    } catch (e) {
      enqueueSnackbar(getErrorMessage(e), SnackBarConfig.getError());
      return [];
    }
  };

  // Fetch studies for each folder
  const fetchStudiesForAllFolders = async (folders: any[]) => {
    try {
      const studyData: Record<string, any[]> = {};

      // Fetch studies for each folder
      const studyPromises = folders.map(async folder => {
        const data: any = {
          patient_id: props?.patient_id,
          legacy_app: folder.value,
          sql_data: subModuleUIData
        };

        const encryptRequestData = EncryptUtils.encryptedData(data);
        const response = await getDicomStudiesData({tk: encryptRequestData});

        const studyNodes = response?.data?.map((study: any) => ({
          value: study.StudyInstanceUID,
          label: study.StudyInfo || 'Series'
        }));

        studyData[folder.value] = studyNodes;
      });

      await Promise.all(studyPromises);

      // Update nodesData to include children (studies)
      setNodesData((prevNodes: any) =>
        prevNodes.map((node: any) => ({
          ...node,
          children: studyData[node.value] || []
        }))
      );
      setIsLoading(false);
    } catch (e) {
      enqueueSnackbar(getErrorMessage(e), SnackBarConfig.getError());
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const dicomData: any = [...SelectedDicomState.selectionData];

    if (dicomData?.length > 0) {
      setChecked(dicomData);
    }

    const fetchData = async () => {
      const folders = await fetchFolders();
      if (folders.length > 0) {
        await fetchStudiesForAllFolders(folders);
      } else {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCheck = (checkedValue: any) => {
    setChecked(checkedValue);
    setSelectedDicomData({data: checkedValue});
  };

  const icons = {
    check: (
      <CheckBoxIcon
        sx={{
          color: theme.palette.primary.main
        }}
        className="rct-icon rct-icon-check"
      />
    ),
    uncheck: (
      <CheckBoxOutlineBlankIcon
        style={{
          color: theme.palette.grey[600]
        }}
        className="rct-icon rct-icon-uncheck"
      />
    ),
    halfCheck: (
      <IndeterminateCheckBoxIcon
        sx={{
          color: theme.palette.primary.main
        }}
        className="rct-icon rct-icon-half-check"
      />
    ),
    expandClose: (
      <ArrowRightIcon
        sx={{
          color: theme.palette.grey[600]
        }}
        className="rct-icon rct-icon-expand-open"
      />
    ),
    expandOpen: (
      <ArrowDropDownIcon
        sx={{
          color: theme.palette.grey[600]
        }}
        className="rct-icon rct-icon-expand-close"
      />
    ),
    parentClose: (
      <FileThumbnail
        file="folder"
        sx={{width: 20, height: 20, marginBottom: '4px'}}
      />
    ),
    parentOpen: (
      <FileThumbnail
        file="openfolder"
        sx={{width: 20, height: 20, marginBottom: '4px'}}
      />
    ),
    leaf: (
      <FileThumbnail
        file="dcm"
        sx={{width: 20, height: 20, marginBottom: '4px'}}
      />
    )
  };

  const renderDicom = () => {
    if (isLoading) {
      return (
        <LoaderGrid>
          <ComponentLoader loaderSize={10} />
        </LoaderGrid>
      );
    }

    if (nodesData.length > 0) {
      return (
        <CheckboxTree
          nodes={nodesData}
          checked={checked}
          expanded={expanded}
          onCheck={handleCheck}
          onExpand={isExpanded => setExpanded(isExpanded)}
          icons={icons}
        />
      );
    }

    return (
      <LoaderGrid>
        {t('PRIVATE.REPORTS.NEW_REPORTS.NO_DATA', {
          ns: 'private'
        })}
      </LoaderGrid>
    );
  };

  return <Grid>{renderDicom()}</Grid>;
};

export default ClinicalReportDicom;
